import { getSerialized } from '@extension/wa-communication-layer/core/utils/wa-key-serialization';
import { ViewFilter } from "@common-components/tasks-panel/filter-button/advanced_filters_shadcn";
import { ExtraDataFile, ExtraDataItem } from "@common-models/ExtraDataItem";
import { Automation, AutomationActions, AutomationConditions, AutomationTrigger } from "@common-models/automation";
import { Board } from "@common-models/board";
import { BlueticksBoardUser } from "@common-models/board-user";
import { BoardView } from "@common-models/board-views";
import { Bot } from "@common-models/bot";
import { BotDeployment } from '@common-models/bot-deployment';
import { ContactLabel } from "@common-models/contact-label";
import { EntitySubscriber } from "@common-models/entity-subscriber";
import { Feature } from "@common-models/feature";
import { BlueticksForm } from "@common-models/form";
import { Gantt } from "@common-models/gantt";
import { Invoice } from "@common-models/invoice";
import { Kanban } from "@common-models/kanban";
import { _Label } from "@common-models/label";
import { Log } from "@common-models/logs";
import { MessageLog } from "@common-models/message-log";
import { CampaignLog } from "@bt-common/schemas/bt/campaign-log";
import { MyWork } from "@common-models/mywork";
import { NewTabFocus } from "@common-models/new-tab-focus";
import { Notification } from "@common-models/notification";
import { QuickLaunch } from "@common-models/quick-launch";
import { RecycledItem } from "@common-models/recycle-items";
import { Subscription } from "../../bt-common/schemas/bt/subscription";
import { Task } from "@common-models/task";
import { TaskColumn } from "@common-models/task-column";
import { TaskColumnOption } from "@common-models/task-column-option";
import { TasksGroup } from "@common-models/task-group";
import { TimeTrackingSession } from "@common-models/time-tracking-session";
import { Tracing } from "@common-models/tracing";
import { UserInterface } from "@common-models/user";
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import { WaContact } from "@common-models/waContact";
import { Webhook } from "@common-models/webhook";
import { Workload } from "@common-models/workload";
import { Workspace } from "@common-models/workspace";
import { BlueticksWorkspaceUser } from "@common-models/workspace-user";
import { Chat } from "@bt-common/interfaces/whatsapp-types";
import { Reminder } from "@common/models/reminder";
import { AllocatedPhoneNumber } from "@common-models/allocated-phone-number";
import { AuthToken } from "@/common/models/auth-token";
import { WaNote } from "@common-models/WaNote";

import { _DBReducerMap, createDBServiceSlice } from "@common-reducers/DBServiceReducers";
import { BulkList } from "@common/models/bulk-list";
import { Campaign } from "@common/models/campaign-model";
import { Drip } from "@bt-common/schemas/bt/drip";
import { DripTemplate } from "@common/models/drip-template";
import { ContactInfo } from "@common/models/contact-info";
import { SessionEvent } from "@common/models/session-event";
import { Template } from "@common/models/template";
import { CommonRootState } from "@common/types/common-root-state-type";
import { NullableId } from "@common/types/interface";
import {
  ActionReducerMapBuilder,
  EntityId,
  Selector,
  createAction,
  createSelector,
  createAsyncThunk
} from "@reduxjs/toolkit";
import sift from "sift";
import { SnippetHooks } from "./hooks/snippet.hook";
import { UserMessageHooks } from "./hooks/user-message.hook";
import { isSendCriticalUserMessage } from "@common/services/send-horizon";
import { WaChat } from "@common/models/waChat";
import { NodeEvent } from "@common/models/node-event";
import { AutomationSession } from "@common/models/automation-session";
import { KnowledgeBase } from "@common/models/knowledge-base";
import { KnowledgeBaseEntry } from "@common/models/knowledge-base-entry";
import { KnowledgeBaseDocument } from "@common/models/knowledge-base-document";
import { Session } from "@common/models/session";
import { CustomPrompt } from "@common/models/custom-prompt";

const initialState = { ids: [], entities: {}, itemsLoaded: false, itemsLoading: false, itemsError: null, itemsLoadedLocaly: false, total: 0, skip: 0, limit: 0 };

// Custom Prompts Reducer
export const {
  slice: CustomPromptsSlice,
  dbThunks: _CustomPromptsThunks,
  selectByQuery: _customPromptsSelectByQuery,
  selectOneObjectByQuery: _customPromptsSelectOneObjectByQuery,
  selectors: _customPromptsSelectors,
} = createDBServiceSlice<CustomPrompt>({
  service: "custom-prompts",
  initialState: initialState,
  getReducerState: (state) => state.DBCustomPromptsReducer,
});
export const DBCustomPromptsReducer = CustomPromptsSlice.reducer;
export const CustomPromptsReducerActions = CustomPromptsSlice.actions;
export type customPromptsReducerActions = keyof typeof CustomPromptsReducerActions;
export const DBCustomPromptsThunks = _CustomPromptsThunks;
export const customPromptsSelectors = _customPromptsSelectors;
export const customPromptsSelectByQuery = _customPromptsSelectByQuery;
export const customPromptsSelectOneObjectByQuery = _customPromptsSelectOneObjectByQuery;

// Lists Reducer
export const {
  slice: BulkListSlice,
  dbThunks: _bulkListThunks,
  selectByQuery: _bulkListSelectByQuery,
  selectOneObjectByQuery: _bulkListSelectOneObjectByQuery,
  selectPaginationStatus: _bulkListSelectPaginationStatus,
} = createDBServiceSlice<BulkList>({
  service: "lists",
  initialState: initialState,
  getReducerState: (state) => state.DBBulkListReducer,
});
export const DBBulkListReducer = BulkListSlice.reducer;
export const bulkListReducerActions = BulkListSlice.actions;
export type BulkListReducerActions = keyof typeof bulkListReducerActions;
export const DBBulkListThunks = _bulkListThunks;
export const bulkListSelectByQuery = _bulkListSelectByQuery;
export const BulkListSelectPaginationStatus = _bulkListSelectPaginationStatus;

// Contact Info Reducer
export const {
  slice: ContactSlice,
  dbThunks: _contactThunks,
  selectByQuery: _contactSelectByQuery,
  selectOneObjectByQuery: _contactSelectOneObjectByQuery,
} = createDBServiceSlice<ContactInfo>({
  service: "contacts",
  initialState: initialState,
  getReducerState: (state) => state.DBContactReducer,
});
export const DBContactReducer = ContactSlice.reducer;
export const contactReducerActions = ContactSlice.actions;
export type ContactReducerActions = keyof typeof contactReducerActions;
export const DBContactThunks = _contactThunks;
export const contactSelectByQuery = _contactSelectByQuery;

// Template Reducer
export const {
  slice: TemplateSlice,
  dbThunks: _templateThunks,
  selectByQuery: _templateSelectByQuery,
  selectOneObjectByQuery: _templateSelectOneObjectByQuery,
  selectors: _templatesSelectors,
} = createDBServiceSlice<Template>({
  service: "templates",
  initialState: initialState,
  getReducerState: (state) => state.DBTemplateReducer,
  hooks: SnippetHooks
});
export const DBTemplateReducer = TemplateSlice.reducer;
export const templateReducerActions = TemplateSlice.actions;
export type TemplateReducerActions = keyof typeof templateReducerActions;
export const DBTemplateThunks = _templateThunks;

/**
 * Query every template list must send. WITHOUT IT YOU GET TEN.
 *
 * The templates service paginates at the app default (`paginate.default = 10`),
 * so a find with no `$limit` silently returns the first page — and with no
 * `$sort` that page is natural order, i.e. the OLDEST ten. Every template a
 * user made after their tenth was invisible in both the management list and
 * the picker; a newly saved template did not appear at all, which is how this
 * got reported ("it only shows the latest 10" — actually the oldest 10).
 *
 * `$limit` is measured, not guessed: on prod the busiest account has 1,343
 * non-deleted templates and only 10 accounts exceed 500, so 500 (the figure the
 * equivalent drips fix uses) would still truncate. 2,000 clears the observed
 * max with room and stays well under the service's `paginate.max` of 5,000,
 * which would reject a larger ask outright.
 *
 * `$sort` is newest-first so that if a future account ever does exceed the cap,
 * what falls off the end is the oldest rather than the template they just made.
 */
export const TEMPLATE_LIST_LIMIT = 2000;

export const TEMPLATE_LIST_QUERY = {
  $sort: { createdAt: -1 },
  $limit: TEMPLATE_LIST_LIMIT,
} as const;

// FE#331 — same shape as TEMPLATE_LIST_QUERY, for the drip-template picker.
export const DRIP_TEMPLATE_LIST_QUERY = {
  $sort: { updatedAt: -1 },
  $limit: TEMPLATE_LIST_LIMIT,
} as const;
export const templateSelectByQuery = _templateSelectByQuery;
export const templateSelectors = _templatesSelectors;
export const templateSelectOneObjectByQuery = _templateSelectOneObjectByQuery;


// Campaigns Reducer
export const {
  slice: CampaignSlice,
  dbThunks: _campaignThunks,
  selectByQuery: _campaignsSelectByQuery,
  selectOneObjectByQuery: _campaignsSelectOneObjectByQuery,
  selectors: _campaignSelectors,
} = createDBServiceSlice<Campaign>({
  service: "campaigns",
  initialState: initialState,
  getReducerState: (state) => state.DBCampaignReducer,
  // customReducers: (builder) => {
  //   builder.addMatcher(
  //     backendApi.endpoints.startCampaign.matchFulfilled,
  //     (state: CommonRootState, { payload }) => {
  //       if (payload.campaignMessages?.length > 0) {
  //       }
  //     }
  //   );
  // },
});
export const DBCampaignReducer = CampaignSlice.reducer;
export const campaignReducerActions = CampaignSlice.actions;
export type CampaignReducerActions = keyof typeof campaignReducerActions;
export const DBCampaignThunks = _campaignThunks;
export const campaignSelectors = _campaignSelectors;
export const campaignsSelectByQuery = _campaignsSelectByQuery;
export const campaignsSelectOneObjectByQuery = _campaignsSelectOneObjectByQuery;


// Drips Reducer — a drip is run as a sequence of campaigns; this slice is the
// drip definition/state. Same dbServiceReducer pattern as campaigns (optimistic
// create + MQTT realtime; the backend bumps `version` so the realtime guard works).
export const {
  slice: DripSlice,
  dbThunks: _dripThunks,
  selectByQuery: _dripsSelectByQuery,
  selectOneObjectByQuery: _dripsSelectOneObjectByQuery,
  selectors: _dripSelectors,
} = createDBServiceSlice<Drip>({
  service: "drips",
  initialState: initialState,
  getReducerState: (state) => state.DBDripReducer,
});
export const DBDripReducer = DripSlice.reducer;
export const dripReducerActions = DripSlice.actions;
export type DripReducerActions = keyof typeof dripReducerActions;
export const DBDripThunks = _dripThunks;
export const dripSelectors = _dripSelectors;
export const dripsSelectByQuery = _dripsSelectByQuery;
export const dripsSelectOneObjectByQuery = _dripsSelectOneObjectByQuery;


// Drip Templates Reducer (FE#331) — a saved, reusable drip DEFINITION (ordered
// steps + inter-step gaps, no audience/schedule/runtime). Own collection so the
// drip orchestrator never touches it. Same dbServiceReducer pattern as drips
// (optimistic create + MQTT realtime; the backend bumps `version`).
export const {
  slice: DripTemplateSlice,
  dbThunks: _dripTemplateThunks,
  selectByQuery: _dripTemplatesSelectByQuery,
  selectOneObjectByQuery: _dripTemplatesSelectOneObjectByQuery,
  selectors: _dripTemplateSelectors,
} = createDBServiceSlice<DripTemplate>({
  service: "drip-templates",
  initialState: initialState,
  getReducerState: (state) => state.DBDripTemplateReducer,
});
export const DBDripTemplateReducer = DripTemplateSlice.reducer;
export const dripTemplateReducerActions = DripTemplateSlice.actions;
export type DripTemplateReducerActions = keyof typeof dripTemplateReducerActions;
export const DBDripTemplateThunks = _dripTemplateThunks;
export const dripTemplateSelectors = _dripTemplateSelectors;
export const dripTemplatesSelectByQuery = _dripTemplatesSelectByQuery;
export const dripTemplatesSelectOneObjectByQuery = _dripTemplatesSelectOneObjectByQuery;


// Message Log Reducer
export const {
  slice: MessageLogSlice,
  dbThunks: _messageLogThunks,
  selectByQuery: _messageLogSelectByQuery,
  selectOneObjectByQuery: _messageLogSelectOneObjectByQuery,
  selectPaginationStatus: _messageLogSelectPaginationStatus,
} =
  createDBServiceSlice<MessageLog>({
    service: "message-logs",
    initialState: initialState,
    getReducerState: (state) => state.DBMessageLogReducer,
    //hooks: UserMessageHooks,
    // R12-B retention cap: message logs append-only across a long WA session and
    // are never pruned client-side — the audit's top unbounded-growth vector.
    // Cap far above any rendered window; oldest rows evict past it. See
    // createDBServiceSlice `maxEntities` docs.
    maxEntities: 10000,
  });
export const DBMessageLogReducer = MessageLogSlice.reducer;
export const messageLogReducerActions = MessageLogSlice.actions;
export type MessageLogReducerActions = keyof typeof messageLogReducerActions;
export const DBMessageLogThunks = _messageLogThunks;
export const messageLogSelectByQuery = _messageLogSelectByQuery;
export const messageLogSelectOneObjectByQuery = _messageLogSelectOneObjectByQuery;
export const messageLogSelectPaginationStatus = _messageLogSelectPaginationStatus;


// Campaign Log Reducer
export const {
  slice: CampaignLogSlice,
  dbThunks: _campaignLogThunks,
  selectByQuery: _campaignLogSelectByQuery,
  selectOneObjectByQuery: _campaignLogSelectOneObjectByQuery,
} =
  createDBServiceSlice<CampaignLog & { _id?: string }>({
    service: "campaign-logs",
    initialState: initialState,
    getReducerState: (state) => state.DBCampaignLogReducer,
    // R19 memory: the untreated TWIN of message-logs — R12-C capped message-logs
    // + user-messages but MISSED this one. campaign-logs append-only on every
    // campaign send and never prune client-side → top long-session ("stuck after
    // hours") unbounded-growth vector in the 24/7 WA-tab store. Same cap +
    // non-destructive eviction (cache, not source of truth; evicted rows re-fetch
    // on demand). Cap far above any rendered window; oldest rows evict past it.
    maxEntities: 10000,
  });
export const DBCampaignLogReducer = CampaignLogSlice.reducer;
export const campaignLogReducerActions = CampaignLogSlice.actions;
export type CampaignLogReducerActions = keyof typeof campaignLogReducerActions;
export const DBCampaignLogThunks = _campaignLogThunks;
export const campaignLogSelectByQuery = _campaignLogSelectByQuery;
export const campaignLogSelectOneObjectByQuery = _campaignLogSelectOneObjectByQuery;


// Taskgroups Reducer
export const {
  slice: TaskgroupsSlice,
  dbThunks: _taskgroupsThunks,
  selectByQuery: _taskGroupsSelectByQuery,
  selectOneObjectByQuery: _taskGroupsSelectOneObjectByQuery,
  selectOneFieldByQuery: _taskGroupsSelectOneFieldByQuery,
  selectByQueryOnlyLength: _taskGroupsSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _taskGroupsSelectByQueryOnlyMaxMin,
  selectOneObjectById: _taskGroupsSelectOneObjectById,
  selectOneFieldById: _taskGroupsSelectOneFieldById,
  selectors: _taskgroupsSelectors,
} = createDBServiceSlice<TasksGroup>({
  service: "task-groups",
  initialState: initialState,
  getReducerState: (state) => state.DBTaskgroupsReducer,
});
export const DBTaskgroupsReducer = TaskgroupsSlice.reducer;

export const taskgroupsReducerActions = TaskgroupsSlice.actions;
export type TaskgroupsReducerActions = keyof typeof taskgroupsReducerActions;
export const DBTaskgroupsThunks = _taskgroupsThunks;
export const taskgroupsSelectors = _taskgroupsSelectors;
export const taskGroupsSelectByQuery = _taskGroupsSelectByQuery;
export const taskGroupsSelectOneFieldByQuery = _taskGroupsSelectOneFieldByQuery;
export const taskGroupsSelectOneObjectByQuery = _taskGroupsSelectOneObjectByQuery;
export const taskGroupsSelectByQueryOnlyLength = _taskGroupsSelectByQueryOnlyLength;
export const taskGroupsSelectByQueryOnlyMaxMin = _taskGroupsSelectByQueryOnlyMaxMin;
export const taskGroupsSelectOneObjectById = _taskGroupsSelectOneObjectById;
export const taskGroupsSelectOneFieldById = _taskGroupsSelectOneFieldById;


// Boards Reducer
export const {
  slice: BoardsSlice,
  dbThunks: _BoardsThunks,
  selectByQuery: _boardsSelectByQuery,
  selectOneObjectByQuery: _boardsSelectOneObjectByQuery,
  selectOneFieldByQuery: _boardsSelectOneFieldByQuery,
  selectByQueryOnlyLength: _boardsSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _boardsSelectByQueryOnlyMaxMin,
  selectPaginationStatus: _boardsSelectPaginationStatus,
  selectOneObjectById: _boardsSelectOneObjectById,
  selectOneFieldById: _boardsSelectOneFieldById,
  selectors: _boardSelectors,
} = createDBServiceSlice<Board>({
  service: "boards",
  initialState: initialState,
  getReducerState: (state) => state.DBBoardsReducer,
});
export const DBBoardsReducer = BoardsSlice.reducer;
export const boardsReducerActions = BoardsSlice.actions;
export type BoardsReducerActions = keyof typeof boardsReducerActions;
export const DBBoardsThunks = _BoardsThunks;
export const boardsSelectors = _boardSelectors;
export const boardsSelectByQuery = _boardsSelectByQuery;
export const boardsSelectOneObjectByQuery = _boardsSelectOneObjectByQuery;
export const boardsSelectOneFieldByQuery = _boardsSelectOneFieldByQuery;
export const boardsSelectByQueryOnlyLength = _boardsSelectByQueryOnlyLength;
export const boardsSelectByQueryOnlyMaxMin = _boardsSelectByQueryOnlyMaxMin;
export const BoardsSelectPaginationStatus = _boardsSelectPaginationStatus;
export const boardsSelectOneObjectById = _boardsSelectOneObjectById;
export const boardsSelectOneFieldById = _boardsSelectOneFieldById;

/**
 * Query every AUDIENCE list/picker must send. WITHOUT IT YOU GET TEN.
 *
 * Audiences are `boards` (`type: 'audience'`), and the boards service paginates
 * at the app default (`paginate.default = 10`). A find with no `$limit` silently
 * returns the first page, so an account with more than ten audiences sees only
 * ten — the rest are simply absent from the management list AND the campaign
 * audience picker (the client-side search box only filters what was loaded, so
 * it can't reach them either). This is how "my audiences disappeared" gets
 * reported when nothing was deleted.
 *
 * Mirror of `TEMPLATE_LIST_QUERY`. Two traps ruled out by measurement:
 *   - `$limit: -1` does NOT mean "all" for boards — the service clamps it to 1
 *     (verified live: returns a single board), so it is WORSE than the default.
 *   - `$paginate: false` is stripped by the boards hook's DollarKeys allowlist
 *     (`$sort/$limit/$skip/$select` only), so it silently does nothing.
 * A large positive `$limit` is the only lever that works. 2,000 clears the
 * observed per-owner board max (~1,687) with room and stays at/under the
 * service's `paginate.max` of 5,000, which would reject a larger ask outright.
 * `$sort` newest-first so that if an account ever did exceed the cap, what falls
 * off the end is the oldest audience rather than one just created.
 */
export const AUDIENCE_LIST_LIMIT = 2000;

export const AUDIENCE_LIST_QUERY = {
  $sort: { createdAt: -1 },
  $limit: AUDIENCE_LIST_LIMIT,
} as const;


// Tasks Reducer
export const {
  slice: TasksSlice,
  dbThunks: _TasksThunks,
  selectByQuery: _tasksSelectByQuery,
  selectOneObjectByQuery: _tasksSelectOneObjectByQuery,
  selectOneFieldByQuery: _tasksSelectOneFieldByQuery,
  selectByQueryOnlyLength: _tasksSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _tasksSelectByQueryOnlyMaxMin,
  selectOneObjectById: _tasksSelectOneObjectById,
  selectOneFieldById: _tasksSelectOneFieldById,
  selectors: _tasksSelectors,
} = createDBServiceSlice<Task>({
  service: "tasks",
  initialState: initialState,
  getReducerState: (state) => state.DBTasksReducer,
});
export const DBTasksReducer = TasksSlice.reducer;
export const tasksReducerActions = TasksSlice.actions;
export type TasksReducerActions = keyof typeof tasksReducerActions;
export const DBTasksThunks = _TasksThunks;
export const tasksSelectors = _tasksSelectors;
export const tasksSelectByQuery = _tasksSelectByQuery;
export const tasksSelectOneFieldByQuery = _tasksSelectOneFieldByQuery;
export const tasksSelectOneObjectByQuery = _tasksSelectOneObjectByQuery;
export const tasksSelectByQueryOnlyLength = _tasksSelectByQueryOnlyLength;
export const tasksSelectByQueryOnlyMaxMin = _tasksSelectByQueryOnlyMaxMin;
export const tasksSelectOneObjectById = _tasksSelectOneObjectById;
export const tasksSelectOneFieldById = _tasksSelectOneFieldById;


// ExtraData Reducer
export const {
  slice: TasksExtraDataSlice,
  dbThunks: _TasksExtraDataThunks,
  selectByQuery: _tasksExtraDataSelectByQuery,
  selectOneObjectByQuery: _tasksExtraDataSelectOneObjectByQuery,
  selectOneFieldByQuery: _tasksExtraDataSelectOneFieldByQuery,
  selectors: _tasksExtraDataSelectors,
} = createDBServiceSlice<ExtraDataItem>({
  service: "tasks-extra-data",
  initialState: initialState,
  getReducerState: (state) => state.DBTasksExtraDataReducer,
});
export const DBTasksExtraDataReducer = TasksExtraDataSlice.reducer;
export const TasksExtraDataReducerActions = TasksExtraDataSlice.actions;
export type tasksExtraDataReducerActions = keyof typeof TasksExtraDataReducerActions;
export const DBTasksExtraDataThunks = _TasksExtraDataThunks;
export const tasksExtraDataSelectors = _tasksExtraDataSelectors;
export const tasksExtraDataSelectByQuery = _tasksExtraDataSelectByQuery;


// TasksExtraDataFiles Reducer
export const {
  slice: TasksExtraDataFilesSlice,
  dbThunks: _TasksExtraDataFilesThunks,
  selectByQuery: _tasksExtraDataFilesSelectByQuery,
  selectOneObjectByQuery: _tasksExtraDataFilesSelectOneObjectByQuery,
  selectOneFieldByQuery: _tasksExtraDataFilesSelectOneFieldByQuery,
  selectors: _tasksExtraDataFilesSelectors,
} = createDBServiceSlice<ExtraDataFile>({
  service: "tasks-extra-data-files",
  initialState: initialState,
  getReducerState: (state) => state.DBTasksExtraDataFilesReducer,
});

export const DBTasksExtraDataFilesReducer = TasksExtraDataFilesSlice.reducer;
export const TasksExtraDataFilesReducerActions = TasksExtraDataFilesSlice.actions;
export type tasksExtraDataFilesReducerActions = keyof typeof TasksExtraDataFilesReducerActions;
export const DBTasksExtraDataFilesThunks = _TasksExtraDataFilesThunks;
export const tasksExtraDataFilesSelectors = _tasksExtraDataFilesSelectors;
export const tasksExtraDataFilesSelectByQuery = _tasksExtraDataFilesSelectByQuery;

// Label Reducer
export const {
  slice: LabelSlice,
  dbThunks: _LabelThunks,
  selectByQuery: _labelSelectByQuery,
  selectOneObjectByQuery: _labelSelectOneObjectByQuery,
  selectOneFieldByQuery: _labelSelectSelectOneFieldByQuery,
  selectors: _labelSelectors,
  selectByQueryOnlyMaxMin: _selectByQueryOnlyMaxMin
} = createDBServiceSlice<_Label>({
  service: "labels",
  initialState: initialState,
  getReducerState: (state) => state.DBLabelReducer,
});

export const DBLabelReducer = LabelSlice.reducer;
export const LabelReducerActions = LabelSlice.actions;
export type labelReducerActions = keyof typeof LabelReducerActions;
export const DBLabelThunks = _LabelThunks;
export const labelSelectors = _labelSelectors;
export const labelSelectByQuery = _labelSelectByQuery;
export const labelSelectByQueryOnlyMaxMin = _selectByQueryOnlyMaxMin;
export const labelSelectOneObjectByQuery = _labelSelectOneObjectByQuery;


// conatct Label Reducer
// ContactsLabels Reducer
export const {
  slice: ContactsLabelsSlice,
  dbThunks: _ContactsLabelsThunks,
  selectByQuery: _contactsLabelsSelectByQuery,
  selectOneObjectByQuery: _contactsLabelsSelectOneObjectByQuery,
  selectOneFieldByQuery: _contactsLabelsSelectOneFieldByQuery,
  selectors: _contactsLabelsSelectors,
  selectByQueryOnlyLength: _contactsLabelssSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _contactsLabelssSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<ContactLabel>({
  service: "contacts-labels",
  initialState: initialState,
  getReducerState: (state) => state.DBContactsLabelsReducer,
});
export const DBContactsLabelsReducer = ContactsLabelsSlice.reducer;
export const ContactsLabelsReducerActions = ContactsLabelsSlice.actions;
export type contactsLabelsReducerActions = keyof typeof ContactsLabelsReducerActions;
export const DBContactsLabelsThunks = _ContactsLabelsThunks;
export const contactsLabelsSelectors = _contactsLabelsSelectors;
export const contactsLabelsSelectByQuery = _contactsLabelsSelectByQuery;
export const contactsLabelsSelectOneObjectByQuery = _contactsLabelsSelectOneObjectByQuery;
export const contactsLabelsSelectOneFieldByQuery = _contactsLabelsSelectOneFieldByQuery;
export const contactsLabelssSelectByQueryOnlyLength = _contactsLabelssSelectByQueryOnlyLength;
export const contactsLabelssSelectByQueryOnlyMaxMin = _contactsLabelssSelectByQueryOnlyMaxMin;


// Invoice Reducer
export const {
  slice: InvoiceSlice,
  dbThunks: _InvoiceThunks,
  selectByQuery: _invoiceSelectByQuery,
  selectOneObjectByQuery: _invoiceSelectOneObjectByQuery,
  selectOneFieldByQuery: _invoiceSelectOneFieldByQuery,
  selectors: _invoiceSelectors,
} = createDBServiceSlice<Invoice>({
  service: "invoices",
  initialState: initialState,
  getReducerState: (state) => state.DBInvoiceReducer,
});
export const DBInvoiceReducer = InvoiceSlice.reducer;
export const InvoiceReducerActions = InvoiceSlice.actions;
export type invoiceReducerActions = keyof typeof InvoiceReducerActions;
export const DBInvoiceThunks = _InvoiceThunks;
export const invoiceSelectors = _invoiceSelectors;
export const invoiceSelectByQuery = _invoiceSelectByQuery;

// TaskColumn Reducer
export const {
  slice: TaskColumnSlice,
  dbThunks: _TaskColumnThunks,
  selectByQuery: _taskColumnSelectByQuery,
  selectOneObjectByQuery: _taskColumnSelectOneObjectByQuery,
  selectOneFieldByQuery: _taskColumnSelectOneFieldByQuery,
  selectors: _taskColumnSelectors,
  selectByQueryOnlyLength: _taskColumnsSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _taskColumnsSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<TaskColumn>({
  service: "task-columns",
  initialState: initialState,
  getReducerState: (state) => state.DBTaskColumnReducer,
});
export const DBTaskColumnReducer = TaskColumnSlice.reducer;
export const TaskColumnReducerActions = TaskColumnSlice.actions;
export type taskColumnReducerActions = keyof typeof TaskColumnReducerActions;
export const DBTaskColumnThunks = _TaskColumnThunks;
export const taskColumnSelectors = _taskColumnSelectors;
export const taskColumnSelectByQuery = _taskColumnSelectByQuery;
export const taskColumnSelectOneObjectByQuery = _taskColumnSelectOneObjectByQuery;
export const taskColumnSelectOneFieldByQuery = _taskColumnSelectOneFieldByQuery;
export const taskColumnsSelectByQueryOnlyLength = _taskColumnsSelectByQueryOnlyLength;
export const taskColumnsSelectByQueryOnlyMaxMin = _taskColumnsSelectByQueryOnlyMaxMin;

// Workspace Reducer
export const {
  slice: WorkspaceSlice,
  dbThunks: _WorkspaceThunks,
  selectByQuery: _workspaceSelectByQuery,
  selectOneObjectByQuery: _workspaceSelectOneObjectByQuery,
  selectOneFieldByQuery: _workspaceSelectOneFieldByQuery,
  selectors: _workspaceSelectors,
  selectByQueryOnlyLength: _workspacesSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _workspacesSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<Workspace>({
  service: "workspaces",
  initialState: initialState,
  getReducerState: (state) => state.DBWorkspaceReducer,
});
export const DBWorkspaceReducer = WorkspaceSlice.reducer;
export const WorkspaceReducerActions = WorkspaceSlice.actions;
export type workspaceReducerActions = keyof typeof WorkspaceReducerActions;
export const DBWorkspaceThunks = _WorkspaceThunks;
export const workspaceSelectors = _workspaceSelectors;
export const workspaceSelectByQuery = _workspaceSelectByQuery;
export const workspaceSelectOneObjectByQuery = _workspaceSelectOneObjectByQuery
export const workspacesSelectByQueryOnlyLength = _workspacesSelectByQueryOnlyLength;
export const workspacesSelectByQueryOnlyMaxMin = _workspacesSelectByQueryOnlyMaxMin;

export function isTempId(id: NullableId): boolean {
  if (id?.toString().length === 16) return true;
  return false;
}

// TaskColumnOption Reducer
export const {
  slice: TaskColumnOptionSlice,
  dbThunks: _TaskColumnOptionThunks,
  selectByQuery: _taskColumnOptionSelectByQuery,
  selectOneObjectByQuery: _taskColumnOptionSelectOneObjectByQuery,
  selectors: _taskColumnOptionSelectors,
  selectByQueryOnlyLength: _taskColumnOptionSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _taskColumnOptionSelectByQueryOnlyMaxMin,
  selectOneObjectById: _taskColumnOptionSelectOneObjectById,
  selectOneFieldById: _taskColumnOptionSelectOneFieldById,
} = createDBServiceSlice<TaskColumnOption>({
  service: "task-column-options",
  initialState: initialState,
  getReducerState: (state) => state.DBTaskColumnOptionReducer,
});
export const DBTaskColumnOptionReducer = TaskColumnOptionSlice.reducer;
export const TaskColumnOptionReducerActions = TaskColumnOptionSlice.actions;
export type taskColumnOptionReducerActions = keyof typeof TaskColumnOptionReducerActions;
export const DBTaskColumnOptionThunks = _TaskColumnOptionThunks;
export const taskColumnOptionSelectors = _taskColumnOptionSelectors;
export const taskColumnOptionSelectByQuery = _taskColumnOptionSelectByQuery;
export const taskColumnOptionSelectByQueryOnlyLength = _taskColumnOptionSelectByQueryOnlyLength;
export const taskColumnOptionSelectByQueryOnlyMaxMin = _taskColumnOptionSelectByQueryOnlyMaxMin;
export const taskColumnOptionSelectOneObjectById = _taskColumnOptionSelectOneObjectById;
export const taskColumnOptionSelectOneFieldById = _taskColumnOptionSelectOneFieldById;

// Users Reducer
export const {
  slice: UserSlice,
  dbThunks: _UserThunks,
  selectByQuery: _userSelectByQuery,
  selectOneObjectByQuery: _userSelectOneObjectByQuery,
  selectors: _userSelectors,
  selectByQueryOnlyLength: _userSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _userSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<UserInterface>({
  service: "users",
  initialState: initialState,
  getReducerState: (state) => state.DBUserReducer,
});
export const DBUserReducer = UserSlice.reducer;
export const UserReducerActions = UserSlice.actions;
export type userReducerActions = keyof typeof UserReducerActions;
export const DBUserThunks = _UserThunks;
export const userSelectors = _userSelectors;
export const userSelectByQuery = _userSelectByQuery;
export const userSelectByQueryOnlyLength = _userSelectByQueryOnlyLength;
export const userSelectByQueryOnlyMaxMin = _userSelectByQueryOnlyMaxMin;
export const userSelectOneObjectByQuery = _userSelectOneObjectByQuery;

// Kanbans Reducer
export const {
  slice: KanbanSlice,
  dbThunks: _KanbanThunks,
  selectByQuery: _kanbanSelectByQuery,
  selectOneObjectByQuery: _kanbanSelectOneObjectByQuery,
  selectOneFieldByQuery: _kanbanSelectOneFieldByQuery,
  selectors: _kanbanSelectors,
  selectByQueryOnlyLength: _kanbanSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _kanbanSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<Kanban>({
  service: "kanbans",
  initialState: initialState,
  getReducerState: (state) => state.DBKanbanReducer,
});
export const DBKanbanReducer = KanbanSlice.reducer;
export const KanbanReducerActions = KanbanSlice.actions;
export type kanbanReducerActions = keyof typeof KanbanReducerActions;
export const DBKanbanThunks = _KanbanThunks;
export const kanbanSelectors = _kanbanSelectors;
export const kanbanSelectByQuery = _kanbanSelectByQuery;
export const kanbanSelectByQueryOnlyLength = _kanbanSelectByQueryOnlyLength;
export const kanbanSelectByQueryOnlyMaxMin = _kanbanSelectByQueryOnlyMaxMin;
export const kanbanSelectOneObjectByQuery = _kanbanSelectOneObjectByQuery;
export const kanbanSelectOneFieldByQuery = _kanbanSelectOneFieldByQuery;


// Workloads Reducer
export const {
  slice: WorkloadSlice,
  dbThunks: _WorkloadThunks,
  selectByQuery: _workloadSelectByQuery,
  selectOneObjectByQuery: _workloadSelectOneObjectByQuery,
  selectOneFieldByQuery: _workloadSelectOneFieldByQuery,
  selectors: _workloadSelectors,
  selectByQueryOnlyLength: _workloadSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _workloadSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<Workload>({
  service: "workloads",
  initialState: initialState,
  getReducerState: (state) => state.DBWorkloadReducer,
});
export const DBWorkloadReducer = WorkloadSlice.reducer;
export const WorkloadReducerActions = WorkloadSlice.actions;
export type workloadReducerActions = keyof typeof WorkloadReducerActions;
export const DBWorkloadThunks = _WorkloadThunks;
export const workloadSelectors = _workloadSelectors;
export const workloadSelectByQuery = _workloadSelectByQuery;
export const workloadSelectByQueryOnlyLength = _workloadSelectByQueryOnlyLength;
export const workloadSelectByQueryOnlyMaxMin = _workloadSelectByQueryOnlyMaxMin;
export const workloadSelectOneObjectByQuery = _workloadSelectOneObjectByQuery;
export const workloadSelectOneFieldByQuery = _workloadSelectOneFieldByQuery;



// Forms Reducer
export const {
  slice: FormSlice,
  dbThunks: _FormThunks,
  selectByQuery: _formSelectByQuery,
  selectOneObjectByQuery: _formSelectOneObjectByQuery,
  selectors: _formSelectors,
  selectByQueryOnlyLength: _formSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _formSelectByQueryOnlyMaxMin,
  selectOneFieldByQuery: _formSelectOneFieldByQuery,
} = createDBServiceSlice<BlueticksForm>({
  service: "forms",
  initialState: initialState,
  getReducerState: (state) => state.DBFormReducer,
});
export const DBFormReducer = FormSlice.reducer;
export const FormReducerActions = FormSlice.actions;
export type formReducerActions = keyof typeof FormReducerActions;
export const DBFormThunks = _FormThunks;
export const formSelectors = _formSelectors;
export const formSelectByQuery = _formSelectByQuery;
export const formSelectByQueryOnlyLength = _formSelectByQueryOnlyLength;
export const formSelectByQueryOnlyMaxMin = _formSelectByQueryOnlyMaxMin;
export const formSelectOneObjectByQuery = _formSelectOneObjectByQuery;
export const formSelectOneFieldByQuery = _formSelectOneFieldByQuery;

// Gantts Reducer
export const {
  slice: GanttSlice,
  dbThunks: _GanttThunks,
  selectByQuery: _ganttSelectByQuery,
  selectOneObjectByQuery: _ganttSelectOneObjectByQuery,
  selectOneFieldByQuery: _ganttSelectOneFieldByQuery,
  selectors: _ganttSelectors,
  selectByQueryOnlyLength: _ganttSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _ganttSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<Gantt>({
  service: "gantts",
  initialState: initialState,
  getReducerState: (state) => state.DBGanttReducer,
});
export const DBGanttReducer = GanttSlice.reducer;
export const GanttReducerActions = GanttSlice.actions;
export type ganttReducerActions = keyof typeof GanttReducerActions;
export const DBGanttThunks = _GanttThunks;
export const ganttSelectors = _ganttSelectors;
export const ganttSelectByQuery = _ganttSelectByQuery;
export const ganttSelectByQueryOnlyLength = _ganttSelectByQueryOnlyLength;
export const ganttSelectByQueryOnlyMaxMin = _ganttSelectByQueryOnlyMaxMin;
export const ganttSelectOneObjectByQuery = _ganttSelectOneObjectByQuery;
export const ganttSelectOneFieldByQuery = _ganttSelectOneFieldByQuery;


// WorkspaceUsers Reducer
export const {
  slice: WorkspaceUserSlice,
  dbThunks: _WorkspaceUserThunks,
  selectByQuery: _workspaceUserSelectByQuery,
  selectOneObjectByQuery: _workspaceUserSelectOneObjectByQuery,
  selectors: _workspaceUserSelectors,
  selectByQueryOnlyLength: _workspaceUserSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _workspaceUserSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<BlueticksWorkspaceUser>({
  service: "workspace-users",
  initialState: initialState,
  getReducerState: (state) => state.DBWorkspaceUserReducer,
});
export const DBWorkspaceUserReducer = WorkspaceUserSlice.reducer;
export const WorkspaceUserReducerActions = WorkspaceUserSlice.actions;
export type workspaceUserReducerActions = keyof typeof WorkspaceUserReducerActions;
export const DBWorkspaceUserThunks = _WorkspaceUserThunks;
export const workspaceUserSelectors = _workspaceUserSelectors;
export const workspaceUserSelectByQuery = _workspaceUserSelectByQuery;
export const workspaceUserSelectByQueryOnlyLength = _workspaceUserSelectByQueryOnlyLength;
export const workspaceUserSelectByQueryOnlyMaxMin = _workspaceUserSelectByQueryOnlyMaxMin;
export const workspaceUserSelectOneObjectByQuery = _workspaceUserSelectOneObjectByQuery;

// BoardUsers Reducer
export const {
  slice: BoardUserSlice,
  dbThunks: _BoardUserThunks,
  selectByQuery: _boardUserSelectByQuery,
  selectOneObjectByQuery: _boardUserSelectOneObjectByQuery,
  selectors: _boardUserSelectors,
  selectByQueryOnlyLength: _boardUserSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _boardUserSelectByQueryOnlyMaxMin,
  selectOneObjectById: _boardUserSelectOneObjectById,
  selectOneFieldById: _boardUserSelectOneFieldById,
} = createDBServiceSlice<BlueticksBoardUser>({
  service: "board-users",
  initialState: initialState,
  getReducerState: (state) => state.DBBoardUserReducer,
});
export const DBBoardUserReducer = BoardUserSlice.reducer;
export const BoardUserReducerActions = BoardUserSlice.actions;
export type boardUserReducerActions = keyof typeof BoardUserReducerActions;
export const DBBoardUserThunks = _BoardUserThunks;
export const boardUserSelectors = _boardUserSelectors;
export const boardUserSelectByQuery = _boardUserSelectByQuery;
export const boardUserSelectByQueryOnlyLength = _boardUserSelectByQueryOnlyLength;
export const boardUserSelectByQueryOnlyMaxMin = _boardUserSelectByQueryOnlyMaxMin;
export const boardUserSelectOneObjectByQuery = _boardUserSelectOneObjectByQuery;
export const boardUserSelectOneObjectById = _boardUserSelectOneObjectById;
export const boardUserSelectOneFieldById = _boardUserSelectOneFieldById;

// Subscriptions Reducer
export const {
  slice: SubscriptionSlice,
  dbThunks: _SubscriptionThunks,
  selectByQuery: _subscriptionSelectByQuery,
  selectOneObjectByQuery: _subscriptionSelectOneObjectByQuery,
  selectors: _subscriptionSelectors,
  selectByQueryOnlyLength: _subscriptionSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _subscriptionSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<Subscription>({
  service: "subscriptions",
  initialState: initialState,
  getReducerState: (state) => state.DBSubscriptionReducer,
  // Subscription docs are Chargebee-derived and carry NEITHER `version` NOR `updatedAt`,
  // so the default version-only guard can never pass: `addMany` skips the id already in
  // the store and `conditionalUpdateMany` drops the row. Every refetch after the initial
  // load was therefore discarded, and a tab that was open across an upgrade kept rendering
  // the plan it first saw (prod: a Pro purchase still showing "Free Plan" until the tab was
  // reloaded — the response on the wire was already `status: "active"`). The server is the
  // ONLY writer of this collection — nothing writes it optimistically on the client — so
  // the freshly fetched copy is always the authoritative one.
  updateTrigger: "always",
});
export const DBSubscriptionReducer = SubscriptionSlice.reducer;
export const SubscriptionReducerActions = SubscriptionSlice.actions;
export type subscriptionReducerActions = keyof typeof SubscriptionReducerActions;
export const DBSubscriptionThunks = _SubscriptionThunks;
export const subscriptionSelectors = _subscriptionSelectors;
export const subscriptionSelectByQuery = _subscriptionSelectByQuery;
export const subscriptionSelectByQueryOnlyLength = _subscriptionSelectByQueryOnlyLength;
export const subscriptionSelectByQueryOnlyMaxMin = _subscriptionSelectByQueryOnlyMaxMin;
export const subscriptionSelectOneObjectByQuery = _subscriptionSelectOneObjectByQuery;

// RecycledItemss Reducer
export const {
  slice: RecycledItemsSlice,
  dbThunks: _RecycledItemsThunks,
  selectByQuery: _recycledItemsSelectByQuery,
  selectOneObjectByQuery: _recycledItemsSelectOneObjectByQuery,
  selectors: _recycledItemsSelectors,
  selectByQueryOnlyLength: _recycledItemsSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _recycledItemsSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<RecycledItem>({
  service: "recycled-items",
  initialState: initialState,
  getReducerState: (state) => state.DBRecycledItemsReducer,
});
export const DBRecycledItemsReducer = RecycledItemsSlice.reducer;
export const RecycledItemsReducerActions = RecycledItemsSlice.actions;
export type recycledItemsReducerActions = keyof typeof RecycledItemsReducerActions;
export const DBRecycledItemsThunks = _RecycledItemsThunks;
export const recycledItemsSelectors = _recycledItemsSelectors;
export const recycledItemsSelectByQuery = _recycledItemsSelectByQuery;
export const recycledItemsSelectByQueryOnlyLength = _recycledItemsSelectByQueryOnlyLength;
export const recycledItemsSelectByQueryOnlyMaxMin = _recycledItemsSelectByQueryOnlyMaxMin;
export const recycledItemsSelectOneObjectByQuery = _recycledItemsSelectOneObjectByQuery;

// Webhooks Reducer
export const {
  slice: WebhooksSlice,
  dbThunks: _WebhooksThunks,
  selectByQuery: _webhooksSelectByQuery,
  selectOneObjectByQuery: _webhooksSelectOneObjectByQuery,
  selectors: _webhooksSelectors,
  selectByQueryOnlyLength: _webhooksSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _webhooksSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<Webhook>({
  service: "webhooks",
  initialState: initialState,
  getReducerState: (state) => state.DBWebhooksReducer,
  // Webhooks carry no `version` but bump `updatedAt` on every patch
  // (timestamps: true on the backend model). With the default version-only
  // guard, every MQTT entity update after the initial load — i.e. every
  // dashboard toggle — was silently dropped, so the WA tab's event-relevance
  // gate kept using a stale subscription list. Same fix as sessions above.
  updateTrigger: "either",
});
export const DBWebhooksReducer = WebhooksSlice.reducer;
export const WebhooksReducerActions = WebhooksSlice.actions;
export type webhooksReducerActions = keyof typeof WebhooksReducerActions;
export const DBWebhooksThunks = _WebhooksThunks;
export const webhooksSelectors = _webhooksSelectors;
export const webhooksSelectByQuery = _webhooksSelectByQuery;
export const webhooksSelectByQueryOnlyLength = _webhooksSelectByQueryOnlyLength;
export const webhooksSelectByQueryOnlyMaxMin = _webhooksSelectByQueryOnlyMaxMin;
export const webhooksSelectOneObjectByQuery = _webhooksSelectOneObjectByQuery;

// User Message Reducer
export const {
  slice: UserMessageSlice,
  dbThunks: _userMessageThunks,
  selectByQuery: _userMessagesSelectByQuery,
  selectByQueryOnlyLength: _userMessagesSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _userMessagesSelectByQueryOnlyMaxMin,
  selectOneFieldByQuery: _userMessagesSelectOneFieldByQuery,
  selectOneObjectByQuery: _userMessagesSelectOneObjectByQuery,
  selectors: _userMessagesSelectors,
} = createDBServiceSlice<UserMessage>({
  service: "user-messages",
  initialState: initialState,
  getReducerState: (state) => state.DBUserMessageReducer,
  // R12-B retention cap: user-messages grows unbounded over a long session (one
  // row per scheduled/sent message). Same rationale + cap as message-logs; see
  // createDBServiceSlice `maxEntities` docs.
  maxEntities: 10000,
  hooks: UserMessageHooks,
  // #270: never evict a send-critical row (pending/in-flight and due inside the send horizon).
  isEvictionProtected: isSendCriticalUserMessage,
});
export const DBUserMessageReducer = UserMessageSlice.reducer;
export const userMessageReducerActions = UserMessageSlice.actions;
export type UserMessageReducerActions = keyof typeof userMessageReducerActions;
export const DBUserMessageThunks = _userMessageThunks;
export const userMessagesSelectors = _userMessagesSelectors;
export const userMessagesSelectByQuery = _userMessagesSelectByQuery;
export const userMessagesSelectByQueryOnlyLength = _userMessagesSelectByQueryOnlyLength;
export const userMessagesSelectByQueryOnlyMaxMin = _userMessagesSelectByQueryOnlyMaxMin;
export const userMessagesSelectOneObjectByQuery = _userMessagesSelectOneObjectByQuery;


export const currentChatMessageListSelector = createSelector<
  [Selector<CommonRootState, CommonRootState>, Selector<CommonRootState, Chat | null>],
  UserMessage[]
>(
  [(state: CommonRootState) => state, (state: CommonRootState) => state.WhatsAppReducer.currentChat],
  (state: CommonRootState, currentChat) =>
    state.DBUserMessageReducer.ids
      .map((id: EntityId) => {
        return state.DBUserMessageReducer.entities[id];
      })
      .filter((m): m is UserMessage => m !== undefined)
      .filter(
        sift({
          $or: [{ deleted: { $exists: false } }, { deleted: false }],
          contactList: { $elemMatch: { id: getSerialized(currentChat?.id) } },
        })
      )
);

export const {
  slice: RemindersSlice,
  dbThunks: DBReminderThunks,
  selectByQuery: reminderSelectByQuery,
  selectOneObjectByQuery: reminderSelectOneObjectByQuery,
  selectOneFieldByQuery: reminderSelectOneFieldByQuery,
  selectors: reminderSelectors,
} = createDBServiceSlice<Reminder>({
  service: "reminders",
  initialState: initialState,
  getReducerState: (state) => state.DBRemindersReducer,
});
export const DBRemindersReducer = RemindersSlice.reducer;
export const RemindersReducerActions = RemindersSlice.actions;
export type remindersReducerActions = keyof typeof RemindersReducerActions;
export const DBRemindersThunks = DBReminderThunks;
export const remindersSelectors = reminderSelectors;
export const remindersSelectByQuery = reminderSelectByQuery;
export const remindersSelectOneObjectByQuery = reminderSelectOneObjectByQuery;


//quick launch reducer 
export const {
  slice: quickLaunchSlice,
  dbThunks: DBQuickLaunchThunks,
  selectByQuery: quickLaunchSelectByQuery,
  selectOneObjectByQuery: quickLaunchSelectOneObjectByQuery,
  selectOneFieldByQuery: quickLaunchSelectOneFieldByQuery,
  selectors: quickLaunchSelectors,
  selectByQueryOnlyLength: quickLaunchSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: quickLaunchSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<QuickLaunch>({
  service: 'quick-launch',
  initialState: initialState,
  getReducerState: (state) => state.DBQuickLaunchReducer
})

export const DBQuickLaunchReducer = quickLaunchSlice.reducer
export const QuickLaunchReducerActions = quickLaunchSlice.actions


// Time tracking cell Reducer
export const {
  slice: TimeTrackingSessionsSlice,
  dbThunks: _TimeTrackingSessionsThunks,
  selectByQuery: _TimeTrackingSessionsSelectByQuery,
  selectOneObjectByQuery: _TimeTrackingSessionsSelectOneObjectByQuery,
  selectors: _TimeTrackingSessionsSelectors,
  selectByQueryOnlyLength: _TimeTrackingSessionsSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _TimeTrackingSessionsSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<TimeTrackingSession>({
  service: "time-tracking-sessions",
  initialState: initialState,
  getReducerState: (state) => state.DBTimeTrackingSessionReducer,
});



export const DBTimeTrackingSessionReducer = TimeTrackingSessionsSlice.reducer;
export const timeTrackingSessionsActions = TimeTrackingSessionsSlice.actions;
export type timeTrackingSessionsReducerActions = keyof typeof timeTrackingSessionsActions;
export const DBTimeTrackingSessionsThunks = _TimeTrackingSessionsThunks;
export const timeTrackingSessionsSelectors = _TimeTrackingSessionsSelectors;
export const timeTrackingSessionsSelectByQuery = _TimeTrackingSessionsSelectByQuery;
export const timeTrackingSessionsSelectByQueryOnlyLength = _TimeTrackingSessionsSelectByQueryOnlyLength;
export const timeTrackingSessionsSelectByQueryOnlyMaxMin = _TimeTrackingSessionsSelectByQueryOnlyMaxMin;
export const timeTrackingSessionsSelectOneObjectByQuery = _TimeTrackingSessionsSelectOneObjectByQuery;

// Log Reducer
export const {
  slice: LogsSlice,
  dbThunks: _LogsThunks,
  selectByQuery: _LogsSelectByQuery,
  selectOneObjectByQuery: _LogsSelectOneObjectByQuery,
  selectors: _LogsSelectors,
  selectByQueryOnlyLength: _LogsSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _LogsSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<Log>({
  service: "logs",
  initialState: initialState,
  getReducerState: (state) => state.DBLogReducer,
});
export const DBLogReducer = LogsSlice.reducer;
export const logsActions = LogsSlice.actions;
export type logsReducerActions = keyof typeof logsActions;
export const DBLogsThunks = _LogsThunks;
export const logsSelectors = _LogsSelectors;
export const logsSelectByQuery = _LogsSelectByQuery;
export const logsSelectByQueryOnlyLength = _LogsSelectByQueryOnlyLength;
export const logsSelectByQueryOnlyMaxMin = _LogsSelectByQueryOnlyMaxMin;
export const logsSelectOneObjectByQuery = _LogsSelectOneObjectByQuery;

// Automation Reducer
export const {
  slice: AutomationsSlice,
  dbThunks: _AutomationsThunks,
  selectByQuery: _AutomationsSelectByQuery,
  selectOneObjectByQuery: _AutomationsSelectOneObjectByQuery,
  selectors: _AutomationsSelectors,
  selectByQueryOnlyLength: _AutomationsSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _AutomationsSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<Automation>({
  service: "automations",
  initialState: initialState,
  getReducerState: (state) => state.DBAutomationReducer,
});
export const DBAutomationReducer = AutomationsSlice.reducer;
export const automationsActions = AutomationsSlice.actions;
export type automationsReducerActions = keyof typeof automationsActions;
export const DBAutomationsThunks = _AutomationsThunks;
export const automationsSelectors = _AutomationsSelectors;
export const automationsSelectByQuery = _AutomationsSelectByQuery;
export const automationsSelectByQueryOnlyLength = _AutomationsSelectByQueryOnlyLength;
export const automationsSelectByQueryOnlyMaxMin = _AutomationsSelectByQueryOnlyMaxMin;
export const automationsSelectOneObjectByQuery = _AutomationsSelectOneObjectByQuery;


// AutomationTrigger Reducer
export const {
  slice: AutomationTriggerSlice,
  dbThunks: _AutomationTriggerThunks,
  selectByQuery: _AutomationTriggerSelectByQuery,
  selectOneObjectByQuery: _AutomationTriggerSelectOneObjectByQuery,
  selectors: _AutomationTriggerSelectors,
  selectByQueryOnlyLength: _AutomationTriggerSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _AutomationTriggerSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<AutomationTrigger>({
  service: "automations-triggers",
  initialState: initialState,
  getReducerState: (state) => state.DBAutomationTriggerReducer,
});
export const DBAutomationTriggerReducer = AutomationTriggerSlice.reducer;
export const automationTriggerActions = AutomationTriggerSlice.actions;
export type automationTriggerReducerActions = keyof typeof automationTriggerActions;
export const DBAutomationTriggerThunks = _AutomationTriggerThunks;
export const automationTriggerSelectors = _AutomationTriggerSelectors;
export const automationTriggerSelectByQuery = _AutomationTriggerSelectByQuery;
export const automationTriggerSelectByQueryOnlyLength = _AutomationTriggerSelectByQueryOnlyLength;
export const automationTriggerSelectByQueryOnlyMaxMin = _AutomationTriggerSelectByQueryOnlyMaxMin;
export const automationTriggerSelectOneObjectByQuery = _AutomationTriggerSelectOneObjectByQuery;

// AutomationConditions Reducer
export const {
  slice: AutomationConditionsSlice,
  dbThunks: _AutomationConditionsThunks,
  selectByQuery: _AutomationConditionsSelectByQuery,
  selectOneObjectByQuery: _AutomationConditionsSelectOneObjectByQuery,
  selectors: _AutomationConditionsSelectors,
  selectByQueryOnlyLength: _AutomationConditionsSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _AutomationConditionsSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<AutomationConditions>({
  service: "automations-conditions",
  initialState: initialState,
  getReducerState: (state) => state.DBAutomationConditionsReducer,
});
export const DBAutomationConditionsReducer = AutomationConditionsSlice.reducer;
export const automationConditionsActions = AutomationConditionsSlice.actions;
export type automationConditionsReducerActions = keyof typeof automationConditionsActions;
export const DBAutomationConditionsThunks = _AutomationConditionsThunks;
export const automationConditionsSelectors = _AutomationConditionsSelectors;
export const automationConditionsSelectByQuery = _AutomationConditionsSelectByQuery;
export const automationConditionsSelectByQueryOnlyLength = _AutomationConditionsSelectByQueryOnlyLength;
export const automationConditionsSelectByQueryOnlyMaxMin = _AutomationConditionsSelectByQueryOnlyMaxMin;
export const automationConditionsSelectOneObjectByQuery = _AutomationConditionsSelectOneObjectByQuery;


// AutomationActions Reducer
export const {
  slice: AutomationActionsSlice,
  dbThunks: _AutomationActionsThunks,
  selectByQuery: _AutomationActionsSelectByQuery,
  selectOneObjectByQuery: _AutomationActionsSelectOneObjectByQuery,
  selectors: _AutomationActionsSelectors,
  selectByQueryOnlyLength: _AutomationActionsSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _AutomationActionsSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<AutomationActions>({
  service: "automations-actions",
  initialState: initialState,
  getReducerState: (state) => state.DBAutomationActionsReducer,
});
export const DBAutomationActionsReducer = AutomationActionsSlice.reducer;
export const automationActionsActions = AutomationActionsSlice.actions;
export type automationActionsReducerActions = keyof typeof automationActionsActions;
export const DBAutomationActionsThunks = _AutomationActionsThunks;
export const automationActionsSelectors = _AutomationActionsSelectors;
export const automationActionsSelectByQuery = _AutomationActionsSelectByQuery;
export const automationActionsSelectByQueryOnlyLength = _AutomationActionsSelectByQueryOnlyLength;
export const automationActionsSelectByQueryOnlyMaxMin = _AutomationActionsSelectByQueryOnlyMaxMin;
export const automationActionsSelectOneObjectByQuery = _AutomationActionsSelectOneObjectByQuery;



// View filters Reducer
export const {
  slice: ViewFiltersSlice,
  dbThunks: _ViewFiltersThunks,
  selectByQuery: _ViewFiltersSelectByQuery,
  selectOneObjectByQuery: _ViewFiltersSelectOneObjectByQuery,
  selectors: _ViewFiltersSelectors,
  selectByQueryOnlyLength: _ViewFiltersSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _ViewFiltersSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<ViewFilter>({
  service: "view-filters",
  initialState: initialState,
  getReducerState: (state) => state.DBViewFiltersReducer,
});
export const DBViewFiltersReducer = ViewFiltersSlice.reducer;
export const viewFiltersActions = ViewFiltersSlice.actions;
export type viewFiltersReducerActions = keyof typeof viewFiltersActions;
export const DBViewFiltersThunks = _ViewFiltersThunks;
export const viewFiltersSelectors = _ViewFiltersSelectors;
export const viewFiltersSelectByQuery = _ViewFiltersSelectByQuery;
export const viewFiltersSelectByQueryOnlyLength = _ViewFiltersSelectByQueryOnlyLength;
export const viewFiltersSelectByQueryOnlyMaxMin = _ViewFiltersSelectByQueryOnlyMaxMin;
export const viewFiltersSelectOneObjectByQuery = _ViewFiltersSelectOneObjectByQuery;





// boardView Reducer
export const {
  slice: BoardViewSlice,
  dbThunks: _BoardViewThunks,
  selectByQuery: _boardViewSelectByQuery,
  selectOneObjectByQuery: _boardViewSelectOneObjectByQuery,
  selectOneFieldByQuery: _boardViewSelectOneFieldByQuery,
  selectors: _boardViewSelectors,
  selectByQueryOnlyLength: _boardViewSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _boardViewSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<BoardView>({
  service: "board-views",
  initialState: initialState,
  getReducerState: (state) => state.DBBoardViewReducer,
});
export const DBBoardViewReducer = BoardViewSlice.reducer;
export const BoardViewReducerActions = BoardViewSlice.actions;
export type boardViewReducerActions = keyof typeof BoardViewReducerActions;
export const DBBoardViewThunks = _BoardViewThunks;
export const boardViewSelectors = _boardViewSelectors;
export const boardViewSelectByQuery = _boardViewSelectByQuery;
export const boardViewSelectByQueryOnlyLength = _boardViewSelectByQueryOnlyLength;
export const boardViewSelectByQueryOnlyMaxMin = _boardViewSelectByQueryOnlyMaxMin;
export const boardViewSelectOneObjectByQuery = _boardViewSelectOneObjectByQuery;
export const boardViewSelectOneFieldByQuery = _boardViewSelectOneFieldByQuery;


// Feature Reducer
export const {
  slice: FeatureSlice,
  dbThunks: _FeatureThunks,
  selectByQuery: _featureSelectByQuery,
  selectOneObjectByQuery: _featureSelectOneObjectByQuery,
  selectors: _featureSelectors,
  selectByQueryOnlyLength: _featureSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _featureSelectByQueryOnlyMaxMin,
  selectOneFieldByQuery: _featureSelectOneFieldByQuery,
} = createDBServiceSlice<Feature>({
  service: "features",
  initialState: initialState,
  getReducerState: (state) => state.DBFeatureReducer,
});
export const DBFeatureReducer = FeatureSlice.reducer;
export const FeatureReducerActions = FeatureSlice.actions;
export type featureReducerActions = keyof typeof FeatureReducerActions;
export const DBFeatureThunks = _FeatureThunks;
export const featureSelectors = _featureSelectors;
export const featureSelectByQuery = _featureSelectByQuery;
export const featureSelectByQueryOnlyLength = _featureSelectByQueryOnlyLength;
export const featureSelectByQueryOnlyMaxMin = _featureSelectByQueryOnlyMaxMin;
export const featureSelectOneObjectByQuery = _featureSelectOneObjectByQuery;
export const featureSelectOneFieldByQuery = _featureSelectOneFieldByQuery;

// MyWorkss Reducer
export const {
  slice: MyWorksSlice,
  dbThunks: _MyWorksThunks,
  selectByQuery: _myWorksSelectByQuery,
  selectOneObjectByQuery: _myWorksSelectOneObjectByQuery,
  selectOneFieldByQuery: _myWorksSelectOneFieldByQuery,
  selectors: _myWorksSelectors,
  selectByQueryOnlyLength: _myWorksSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _myWorksSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<MyWork>({
  service: "my-works",
  initialState: initialState,
  getReducerState: (state) => state.DBMyWorksReducer,
});
export const DBMyWorksReducer = MyWorksSlice.reducer;
export const MyWorksReducerActions = MyWorksSlice.actions;
export type myWorksReducerActions = keyof typeof MyWorksReducerActions;
export const DBMyWorksThunks = _MyWorksThunks;
export const myWorksSelectors = _myWorksSelectors;
export const myWorksSelectByQuery = _myWorksSelectByQuery;
export const myWorksSelectByQueryOnlyLength = _myWorksSelectByQueryOnlyLength;
export const myWorksSelectByQueryOnlyMaxMin = _myWorksSelectByQueryOnlyMaxMin;
export const myWorksSelectOneObjectByQuery = _myWorksSelectOneObjectByQuery;
export const myWorksSelectOneFieldByQuery = _myWorksSelectOneFieldByQuery;



// Notification Reducer
export const {
  slice: NotificationsSlice,
  dbThunks: _NotificationsThunks,
  selectByQuery: _notificationsSelectByQuery,
  selectOneObjectByQuery: _notificationsSelectOneObjectByQuery,
  selectors: _notificationsSelectors,
  selectByQueryOnlyLength: _notificationsSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _notificationsSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<Notification>({
  service: "notifications",
  initialState: initialState,
  getReducerState: (state) => state.DBNotificationsReducer,
});
export const DBNotificationsReducer = NotificationsSlice.reducer;
export const NotificationsReducerActions = NotificationsSlice.actions;
export type notificationsReducerActions = keyof typeof NotificationsReducerActions;
export const DBNotificationsThunks = _NotificationsThunks;
export const notificationsSelectors = _notificationsSelectors;
export const notificationsSelectByQuery = _notificationsSelectByQuery;
export const notificationsSelectByQueryOnlyLength = _notificationsSelectByQueryOnlyLength;
export const notificationsSelectByQueryOnlyMaxMin = _notificationsSelectByQueryOnlyMaxMin;
export const notificationsSelectOneObjectByQuery = _notificationsSelectOneObjectByQuery;


// EntitySubscribers Reducer
export const {
  slice: EntitySubscribersSlice,
  dbThunks: _EntitySubscribersThunks,
  selectByQuery: _entitySubscribersSelectByQuery,
  selectOneObjectByQuery: _entitySubscribersSelectOneObjectByQuery,
  selectors: _entitySubscribersSelectors,
  selectByQueryOnlyLength: _entitySubscribersSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _entitySubscribersSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<EntitySubscriber>({
  service: "entity-subscribers",
  initialState: initialState,
  getReducerState: (state) => state.DBEntitySubscribersReducer,
});
export const DBEntitySubscribersReducer = EntitySubscribersSlice.reducer;
export const EntitySubscribersReducerActions = EntitySubscribersSlice.actions;
export type entitySubscribersReducerActions = keyof typeof EntitySubscribersReducerActions;
export const DBEntitySubscribersThunks = _EntitySubscribersThunks;
export const entitySubscribersSelectors = _entitySubscribersSelectors;
export const entitySubscribersSelectByQuery = _entitySubscribersSelectByQuery;
export const entitySubscribersSelectByQueryOnlyLength = _entitySubscribersSelectByQueryOnlyLength;
export const entitySubscribersSelectByQueryOnlyMaxMin = _entitySubscribersSelectByQueryOnlyMaxMin;
export const entitySubscribersSelectOneObjectByQuery = _entitySubscribersSelectOneObjectByQuery;


// Sessions Reducer
export const {
  slice: SessionSlice,
  dbThunks: _SessionThunks,
  selectByQuery: _sessionSelectByQuery,
  selectOneObjectByQuery: _sessionSelectOneObjectByQuery,
  selectors: _sessionSelectors,
  selectByQueryOnlyLength: _sessionSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _sessionSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<Session>({
  service: "sessions",
  initialState: initialState,
  getReducerState: (state) => state.DBSessionReducer,
  // Sessions carry no `version` but bump `updatedAt` on every patch (incl. the
  // gateway's ~20s QR rotation). With the default version-only guard, every
  // post-load session update — via MQTT entity-update AND find() refetch alike —
  // is silently dropped, freezing the displayed QR until a full page refresh.
  // Guard on updatedAt (or version) so the live QR tracks each rotation.
  updateTrigger: "either",
});
export const DBSessionReducer = SessionSlice.reducer;
export const SessionReducerActions = SessionSlice.actions;
export type sessionReducerActions = keyof typeof SessionReducerActions;
export const DBSessionThunks = _SessionThunks;
export const sessionSelectors = _sessionSelectors;
export const sessionSelectByQuery = _sessionSelectByQuery;
export const sessionSelectByQueryOnlyLength = _sessionSelectByQueryOnlyLength;
export const sessionSelectByQueryOnlyMaxMin = _sessionSelectByQueryOnlyMaxMin;
export const sessionSelectOneObjectByQuery = _sessionSelectOneObjectByQuery;


//create reducer for NewTabFocus
export const {
  slice: NewTabFocusSlice,
  dbThunks: _NewTabFocusThunks,
  selectByQuery: _newTabFocusSelectByQuery,
  selectOneObjectByQuery: _newTabFocusSelectOneObjectByQuery,
  selectors: _newTabFocusSelectors,
  selectByQueryOnlyLength: _newTabFocusSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _newTabFocusSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<NewTabFocus>({
  service: "new-tab-focus",
  initialState: initialState,
  getReducerState: (state) => state.DBNewTabFocusReducer,
});
export const DBNewTabFocusReducer = NewTabFocusSlice.reducer;
export const NewTabFocusReducerActions = NewTabFocusSlice.actions;
export type newTabFocusReducerActions = keyof typeof NewTabFocusReducerActions;
export const DBNewTabFocusThunks = _NewTabFocusThunks;
export const newTabFocusSelectors = _newTabFocusSelectors;
export const newTabFocusSelectByQuery = _newTabFocusSelectByQuery;
export const newTabFocusSelectByQueryOnlyLength =
  _newTabFocusSelectByQueryOnlyLength;
export const newTabFocusSelectByQueryOnlyMaxMin =
  _newTabFocusSelectByQueryOnlyMaxMin;
export const newTabFocusSelectOneObjectByQuery =
  _newTabFocusSelectOneObjectByQuery;





// Create the reset action for Bots
export const resetBotState = createAction('bot/reset');

// Create the slice for Bots
export const {
  slice: BotSlice,
  dbThunks: _BotThunks,
  selectByQuery: _botSelectByQuery,
  selectOneObjectByQuery: _botSelectOneObjectByQuery,
  selectors: _botSelectors,
  selectByQueryOnlyLength: _botSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _botSelectByQueryOnlyMaxMin,
  selectOneFieldByQuery: _botSelectOneFieldByQuery,
} = createDBServiceSlice<Bot>({
  service: "bot",
  initialState: initialState,
  getReducerState: (state) => state.DBBotReducer,
  customReducers: (builder: ActionReducerMapBuilder<any>) => {
    builder.addCase(resetBotState, () => ({
      ids: [],
      entities: {},
      itemsLoaded: false,
      itemsLoading: false,
      total: 0,
      skip: 0,
      limit: 0,
    }));
  }
});

// Export the reducer
export const DBBotReducer = BotSlice.reducer;

// Export the actions
export const BotReducerActions = BotSlice.actions;
export type botReducerActions = keyof typeof BotReducerActions;

// Export other thunks and selectors
export const DBBotThunks = _BotThunks;
export const botSelectors = _botSelectors;
export const botSelectByQuery = _botSelectByQuery;
export const botSelectByQueryOnlyLength = _botSelectByQueryOnlyLength;
export const botSelectByQueryOnlyMaxMin = _botSelectByQueryOnlyMaxMin;
export const botSelectOneObjectByQuery = (state: any, query: any) => {
  try {
    if (!state || !state.ids || !state.entities) {
      console.warn("Invalid state in botSelectOneObjectByQuery");
      return null;
    }
    return _botSelectOneObjectByQuery(state, query);
  } catch (error) {
    console.error("Error in botSelectOneObjectByQuery:", error);
    return null;
  }
};
export const botSelectOneFieldByQuery = _botSelectOneFieldByQuery;

//create reducer for WaChat
export const {
  slice: WaChatSlice,
  dbThunks: _WaChatThunks,
  selectByQuery: _WaChatSelectByQuery,
  selectOneObjectByQuery: _WaChatSelectOneObjectByQuery,
  selectors: _WaChatSelectors,
  selectByQueryOnlyLength: _WaChatSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _WaChatSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<WaChat>({
  service: "wachats",
  initialState: initialState,
  getReducerState: (state) => state.DBWaChatReducer,
});
export const DBWaChatReducer = WaChatSlice.reducer;
export const WaChatReducerActions = WaChatSlice.actions;
export type waChatReducerActions = keyof typeof WaChatReducerActions;
export const DBWaChatThunks = _WaChatThunks;
export const WaChatSelectors = _WaChatSelectors;
export const WaChatSelectByQuery = _WaChatSelectByQuery;
export const WaChatSelectByQueryOnlyLength = _WaChatSelectByQueryOnlyLength;
export const WaChatSelectByQueryOnlyMaxMin = _WaChatSelectByQueryOnlyMaxMin;
export const WaChatSelectOneObjectByQuery = _WaChatSelectOneObjectByQuery;


// Tracings Reducer
export const {
  slice: TracingsSlice,
  dbThunks: _TracingsThunks,
  selectByQuery: _tracingsSelectByQuery,
  selectOneObjectByQuery: _tracingsSelectOneObjectByQuery,
  selectors: _tracingsSelectors,
  selectByQueryOnlyLength: _tracingsSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _tracingsSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<Tracing>({
  service: "tracing",
  initialState: initialState,
  getReducerState: (state) => state.DBTracingsReducer,
});
export const DBTracingsReducer = TracingsSlice.reducer;
export const TracingsReducerActions = TracingsSlice.actions;
export type tracingsReducerActions = keyof typeof TracingsReducerActions;
export const DBTracingsThunks = _TracingsThunks;
export const tracingsSelectors = _tracingsSelectors;
export const tracingsSelectByQuery = _tracingsSelectByQuery;
export const tracingsSelectByQueryOnlyLength = _tracingsSelectByQueryOnlyLength;
export const tracingsSelectByQueryOnlyMaxMin = _tracingsSelectByQueryOnlyMaxMin;
export const tracingsSelectOneObjectByQuery = _tracingsSelectOneObjectByQuery;


// Session Events Reducer
export const {
  slice: SessionEventsSlice,
  dbThunks: _SessionEventsThunks,
  selectByQuery: _sessionEventsSelectByQuery,
  selectOneObjectByQuery: _sessionEventsSelectOneObjectByQuery,
  selectors: _sessionEventsSelectors,
  selectByQueryOnlyLength: _sessionEventsSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _sessionEventsSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<SessionEvent>({
  service: "session-events",
  initialState: initialState,
  getReducerState: (state) => state.DBSessionEventsReducer,
});

export const DBSessionEventsReducer = SessionEventsSlice.reducer;
export const SessionEventsReducerActions = SessionEventsSlice.actions;
export type sessionEventsReducerActions = keyof typeof SessionEventsReducerActions;
export const DBSessionEventsThunks = _SessionEventsThunks;
export const sessionEventsSelectors = _sessionEventsSelectors;
export const sessionEventsSelectByQuery = _sessionEventsSelectByQuery;
export const sessionEventsSelectByQueryOnlyLength = _sessionEventsSelectByQueryOnlyLength;
export const sessionEventsSelectByQueryOnlyMaxMin = _sessionEventsSelectByQueryOnlyMaxMin;
export const sessionEventsSelectOneObjectByQuery = _sessionEventsSelectOneObjectByQuery;


// Local Sessions Reducer
export const {
  slice: LocalSessionsSlice,
  dbThunks: _LocalSessionsThunks,
  selectByQuery: _localSessionsSelectByQuery,
  selectOneObjectByQuery: _localSessionsSelectOneObjectByQuery,
  selectors: _localSessionsSelectors,
  selectByQueryOnlyLength: _localSessionsSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _localSessionsSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<Session>({
  service: "local-sessions",
  initialState: initialState,
  getReducerState: (state) => state.DBLocalSessionsReducer,
});

export const DBLocalSessionsReducer = LocalSessionsSlice.reducer;
export const LocalSessionsReducerActions = LocalSessionsSlice.actions;
export type localSessionsReducerActions = keyof typeof LocalSessionsReducerActions;
export const DBLocalSessionsThunks = _LocalSessionsThunks;
export const localSessionsSelectors = _localSessionsSelectors;
export const localSessionsSelectByQuery = _localSessionsSelectByQuery;
export const localSessionsSelectByQueryOnlyLength = _localSessionsSelectByQueryOnlyLength;
export const localSessionsSelectByQueryOnlyMaxMin = _localSessionsSelectByQueryOnlyMaxMin;
export const localSessionsSelectOneObjectByQuery = _localSessionsSelectOneObjectByQuery;




// create reducer for WaContactsCache
export const {
  slice: WaContactsCacheSlice,
  dbThunks: _WaContactsCacheThunks,
  selectByQuery: _waContactsCacheSelectByQuery,
  selectOneObjectByQuery: _waContactsCacheSelectOneObjectByQuery,
  selectors: _waContactsCacheSelectors,
} = createDBServiceSlice<WaContact>({
  service: "wa-contacts-cache",
  initialState: initialState,
  getReducerState: (state) => state.DBWaContactsCacheReducer,
});

export const DBWaContactsCacheReducer = WaContactsCacheSlice.reducer;
export const WaContactsCacheReducerActions = WaContactsCacheSlice.actions;
export type waContactsCacheReducerActions = keyof typeof WaContactsCacheReducerActions;
export const DBWaContactsCacheThunks = _WaContactsCacheThunks;
export const waContactsCacheSelectors = _waContactsCacheSelectors;
export const waContactsCacheSelectByQuery = _waContactsCacheSelectByQuery;
export const waContactsCacheSelectOneObjectByQuery = _waContactsCacheSelectOneObjectByQuery;

// create reducer for TeamInboxChatsCache
export const {
  slice: TeamInboxChatsCacheSlice,
  dbThunks: _TeamInboxChatsCacheThunks,
  selectByQuery: _teamInboxChatsCacheSelectByQuery,
  selectOneObjectByQuery: _teamInboxChatsCacheSelectOneObjectByQuery,
  selectors: _teamInboxChatsCacheSelectors,
  selectByQueryOnlyLength: _teamInboxChatsCacheSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _teamInboxChatsCacheSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<any>({
  service: "team-inbox-chats",
  initialState: initialState,
  getReducerState: (state) => state.DBTeamInboxChatsCacheReducer,
});

export const DBTeamInboxChatsCacheReducer = TeamInboxChatsCacheSlice.reducer;
export const TeamInboxChatsCacheReducerActions = TeamInboxChatsCacheSlice.actions;
export type teamInboxChatsCacheReducerActions = keyof typeof TeamInboxChatsCacheReducerActions;
export const DBTeamInboxChatsCacheThunks = _TeamInboxChatsCacheThunks;
export const teamInboxChatsCacheSelectByQuery = _teamInboxChatsCacheSelectByQuery;
export const teamInboxChatsCacheSelectByQueryOnlyLength = _teamInboxChatsCacheSelectByQueryOnlyLength;
export const teamInboxChatsCacheSelectByQueryOnlyMaxMin = _teamInboxChatsCacheSelectByQueryOnlyMaxMin;
export const teamInboxChatsCacheSelectOneObjectByQuery = _teamInboxChatsCacheSelectOneObjectByQuery;

// create reducer for TeamInboxMessagesCache
export const {
  slice: TeamInboxMessagesCacheSlice,
  dbThunks: _TeamInboxMessagesCacheThunks,
  selectByQuery: _teamInboxMessagesCacheSelectByQuery,
  selectOneObjectByQuery: _teamInboxMessagesCacheSelectOneObjectByQuery,
  selectors: _teamInboxMessagesCacheSelectors,
  selectByQueryOnlyLength: _teamInboxMessagesCacheSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _teamInboxMessagesCacheSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<any>({
  service: "team-inbox-messages",
  initialState: initialState,
  getReducerState: (state) => state.DBTeamInboxMessagesCacheReducer,
});

export const DBTeamInboxMessagesCacheReducer = TeamInboxMessagesCacheSlice.reducer;
export const TeamInboxMessagesCacheReducerActions = TeamInboxMessagesCacheSlice.actions;
export type teamInboxMessagesCacheReducerActions = keyof typeof TeamInboxMessagesCacheReducerActions;
export const DBTeamInboxMessagesCacheThunks = _TeamInboxMessagesCacheThunks;
// teamInboxMessagesCacheSelectors moved to @/components/team-inbox/redux/TeamInboxSelectors.ts
export const teamInboxMessagesCacheSelectByQuery = _teamInboxMessagesCacheSelectByQuery;
export const teamInboxMessagesCacheSelectByQueryOnlyLength = _teamInboxMessagesCacheSelectByQueryOnlyLength;
export const teamInboxMessagesCacheSelectByQueryOnlyMaxMin = _teamInboxMessagesCacheSelectByQueryOnlyMaxMin;
export const teamInboxMessagesCacheSelectOneObjectByQuery = _teamInboxMessagesCacheSelectOneObjectByQuery;

// Create the reset action for BotDeployment
export const resetBotDeploymentState = createAction('bot-deployment/reset');

// Create the slice for BotDeployment
export const {
  slice: BotDeploymentSlice,
  dbThunks: _BotDeploymentThunks,
  selectByQuery: _botDeploymentSelectByQuery,
  selectOneObjectByQuery: _botDeploymentSelectOneObjectByQuery,
  selectors: _botDeploymentSelectors,
  selectByQueryOnlyLength: _botDeploymentSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _botDeploymentSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<BotDeployment>({
  service: "bot-deployment",
  initialState: initialState,
  getReducerState: (state) => state.DBBotDeploymentReducer,
  customReducers: (builder: ActionReducerMapBuilder<any>) => {
    builder.addCase(resetBotDeploymentState, () => ({
      ids: [],
      entities: {},
      itemsLoaded: false,
      itemsLoading: false,
      total: 0,
      skip: 0,
      limit: 0,
    }));
  }
});

// Export the reducer
export const DBBotDeploymentReducer = BotDeploymentSlice.reducer;

// Export the actions
export const BotDeploymentReducerActions = BotDeploymentSlice.actions;
export type botDeploymentReducerActions = keyof typeof BotDeploymentReducerActions;

// Export other thunks and selectors
export const DBBotDeploymentThunks = _BotDeploymentThunks;
export const botDeploymentSelectors = _botDeploymentSelectors;
export const botDeploymentSelectByQuery = _botDeploymentSelectByQuery;
export const botDeploymentSelectByQueryOnlyLength = _botDeploymentSelectByQueryOnlyLength;
export const botDeploymentSelectByQueryOnlyMaxMin = _botDeploymentSelectByQueryOnlyMaxMin;
export const botDeploymentSelectOneObjectByQuery = _botDeploymentSelectOneObjectByQuery;

// Create the reset action for AutomationSession
export const resetAutomationSessionState = createAction('automation-session/reset');

// AutomationSession Reducer
export const {
  slice: AutomationSessionSlice,
  dbThunks: _AutomationSessionThunks,
  selectByQuery: _automationSessionSelectByQuery,
  selectOneObjectByQuery: _automationSessionSelectOneObjectByQuery,
  selectors: _automationSessionSelectors,
  selectByQueryOnlyLength: _automationSessionSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _automationSessionSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<AutomationSession>({
  service: "automation-session",
  initialState: initialState,
  getReducerState: (state) => state.DBAutomationSessionReducer,
  customReducers: (builder: ActionReducerMapBuilder<any>) => {
    builder.addCase(resetAutomationSessionState, () => ({
      ids: [],
      entities: {},
      itemsLoaded: false,
      itemsLoading: false,
      total: 0,
      skip: 0,
      limit: 0,
    }));
  }
});

export const DBAutomationSessionReducer = AutomationSessionSlice.reducer;
export const AutomationSessionReducerActions = AutomationSessionSlice.actions;
export type automationSessionReducerActions = keyof typeof AutomationSessionReducerActions;
export const DBAutomationSessionThunks = _AutomationSessionThunks;
export const automationSessionSelectors = _automationSessionSelectors;
export const automationSessionSelectByQuery = _automationSessionSelectByQuery;
export const automationSessionSelectByQueryOnlyLength = _automationSessionSelectByQueryOnlyLength;
export const automationSessionSelectByQueryOnlyMaxMin = _automationSessionSelectByQueryOnlyMaxMin;
export const automationSessionSelectOneObjectByQuery = _automationSessionSelectOneObjectByQuery;


// create reducer for NodeEvents
export const {
  slice: NodeEventsSlice,
  dbThunks: _NodeEventsThunks,
  selectByQuery: _nodeEventsSelectByQuery,
  selectOneObjectByQuery: _nodeEventsSelectOneObjectByQuery,
  selectors: _nodeEventsSelectors,
  selectByQueryOnlyLength: _nodeEventsSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _nodeEventsSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<NodeEvent>({
  service: "node-events",
  initialState: initialState,
  getReducerState: (state) => state.DBNodeEventsReducer,
});

export const DBNodeEventsReducer = NodeEventsSlice.reducer;
export const NodeEventsReducerActions = NodeEventsSlice.actions;
export type nodeEventsReducerActions = keyof typeof NodeEventsReducerActions;
export const DBNodeEventsThunks = _NodeEventsThunks;
export const nodeEventsSelectors = _nodeEventsSelectors;
export const nodeEventsSelectByQuery = _nodeEventsSelectByQuery;
export const nodeEventsSelectByQueryOnlyLength = _nodeEventsSelectByQueryOnlyLength;
export const nodeEventsSelectByQueryOnlyMaxMin = _nodeEventsSelectByQueryOnlyMaxMin;
export const nodeEventsSelectOneObjectByQuery = _nodeEventsSelectOneObjectByQuery;


// Knowledge Base Reducer
export const {
  slice: KnowledgeBaseSlice,
  dbThunks: _KnowledgeBaseThunks,
  selectByQuery: _knowledgeBaseSelectByQuery,
  selectOneObjectByQuery: _knowledgeBaseSelectOneObjectByQuery,
  selectOneFieldByQuery: _knowledgeBaseSelectOneFieldByQuery,
  selectors: _knowledgeBaseSelectors,
  selectByQueryOnlyLength: _knowledgeBaseSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _knowledgeBaseSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<KnowledgeBase>({
  service: "knowledge-bases",
  initialState: initialState,
  getReducerState: (state) => state.DBKnowledgeBaseReducer,
});

export const DBKnowledgeBaseReducer = KnowledgeBaseSlice.reducer;
export const KnowledgeBaseReducerActions = KnowledgeBaseSlice.actions;
export type knowledgeBaseReducerActions = keyof typeof KnowledgeBaseReducerActions;
export const DBKnowledgeBaseThunks = _KnowledgeBaseThunks;
export const knowledgeBaseSelectors = _knowledgeBaseSelectors;
export const knowledgeBaseSelectByQuery = _knowledgeBaseSelectByQuery;
export const knowledgeBaseSelectByQueryOnlyLength = _knowledgeBaseSelectByQueryOnlyLength;
export const knowledgeBaseSelectByQueryOnlyMaxMin = _knowledgeBaseSelectByQueryOnlyMaxMin;
export const knowledgeBaseSelectOneObjectByQuery = _knowledgeBaseSelectOneObjectByQuery;
export const knowledgeBaseSelectOneFieldByQuery = _knowledgeBaseSelectOneFieldByQuery;

// Knowledge Base Documents Reducer
export const {
  slice: KnowledgeBaseDocumentSlice,
  dbThunks: _KnowledgeBaseDocumentThunks,
  selectByQuery: _knowledgeBaseDocumentSelectByQuery,
  selectOneObjectByQuery: _knowledgeBaseDocumentSelectOneObjectByQuery,
  selectOneFieldByQuery: _knowledgeBaseDocumentSelectOneFieldByQuery,
  selectors: _knowledgeBaseDocumentSelectors,
  selectByQueryOnlyLength: _knowledgeBaseDocumentSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _knowledgeBaseDocumentSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<KnowledgeBaseDocument>({
  service: "knowledge-base-documents",
  initialState: initialState,
  getReducerState: (state) => state.DBKnowledgeBaseDocumentReducer,
});

export const DBKnowledgeBaseDocumentReducer = KnowledgeBaseDocumentSlice.reducer;
export const KnowledgeBaseDocumentReducerActions = KnowledgeBaseDocumentSlice.actions;
export type knowledgeBaseDocumentReducerActions = keyof typeof KnowledgeBaseDocumentReducerActions;
export const DBKnowledgeBaseDocumentThunks = _KnowledgeBaseDocumentThunks;
export const knowledgeBaseDocumentSelectors = _knowledgeBaseDocumentSelectors;
export const knowledgeBaseDocumentSelectByQuery = _knowledgeBaseDocumentSelectByQuery;
export const knowledgeBaseDocumentSelectByQueryOnlyLength = _knowledgeBaseDocumentSelectByQueryOnlyLength;
export const knowledgeBaseDocumentSelectByQueryOnlyMaxMin = _knowledgeBaseDocumentSelectByQueryOnlyMaxMin;
export const knowledgeBaseDocumentSelectOneObjectByQuery = _knowledgeBaseDocumentSelectOneObjectByQuery;
export const knowledgeBaseDocumentSelectOneFieldByQuery = _knowledgeBaseDocumentSelectOneFieldByQuery;

// Knowledge Base Entries Reducer
export const {
  slice: KnowledgeBaseEntrySlice,
  dbThunks: _KnowledgeBaseEntryThunks,
  selectByQuery: _knowledgeBaseEntrySelectByQuery,
  selectOneObjectByQuery: _knowledgeBaseEntrySelectOneObjectByQuery,
  selectOneFieldByQuery: _knowledgeBaseEntrySelectOneFieldByQuery,
  selectors: _knowledgeBaseEntrySelectors,
  selectByQueryOnlyLength: _knowledgeBaseEntrySelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _knowledgeBaseEntrySelectByQueryOnlyMaxMin,
} = createDBServiceSlice<KnowledgeBaseEntry>({
  service: "knowledge-base-entries",
  initialState: initialState,
  getReducerState: (state) => state.DBKnowledgeBaseEntryReducer,
});

export const DBKnowledgeBaseEntryReducer = KnowledgeBaseEntrySlice.reducer;
export const KnowledgeBaseEntryReducerActions = KnowledgeBaseEntrySlice.actions;
export type knowledgeBaseEntryReducerActions = keyof typeof KnowledgeBaseEntryReducerActions;
export const DBKnowledgeBaseEntryThunks = _KnowledgeBaseEntryThunks;
export const knowledgeBaseEntrySelectors = _knowledgeBaseEntrySelectors;
export const knowledgeBaseEntrySelectByQuery = _knowledgeBaseEntrySelectByQuery;
export const knowledgeBaseEntrySelectByQueryOnlyLength = _knowledgeBaseEntrySelectByQueryOnlyLength;
export const knowledgeBaseEntrySelectByQueryOnlyMaxMin = _knowledgeBaseEntrySelectByQueryOnlyMaxMin;
export const knowledgeBaseEntrySelectOneObjectByQuery = _knowledgeBaseEntrySelectOneObjectByQuery;
export const knowledgeBaseEntrySelectOneFieldByQuery = _knowledgeBaseEntrySelectOneFieldByQuery;

// AllocatedPhoneNumber Reducer
export const {
  slice: AllocatedPhoneNumberSlice,
  dbThunks: _allocatedPhoneNumberThunks,
  selectByQuery: _allocatedPhoneNumberSelectByQuery,
  selectOneObjectByQuery: _allocatedPhoneNumberSelectOneObjectByQuery,
  selectors: _allocatedPhoneNumberSelectors,
  selectByQueryOnlyLength: _allocatedPhoneNumberSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _allocatedPhoneNumberSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<AllocatedPhoneNumber>({
  service: "allocated-phone-numbers",
  initialState: initialState,
  getReducerState: (state) => state.DBAllocatedPhoneNumberReducer,
});

export const DBAllocatedPhoneNumberReducer = AllocatedPhoneNumberSlice.reducer;
export const AllocatedPhoneNumberReducerActions = AllocatedPhoneNumberSlice.actions;
export type allocatedPhoneNumberReducerActions = keyof typeof AllocatedPhoneNumberReducerActions;
export const DBAllocatedPhoneNumberThunks = _allocatedPhoneNumberThunks;
export const allocatedPhoneNumberSelectors = _allocatedPhoneNumberSelectors;
export const allocatedPhoneNumberSelectByQuery = _allocatedPhoneNumberSelectByQuery;
export const allocatedPhoneNumberSelectByQueryOnlyLength = _allocatedPhoneNumberSelectByQueryOnlyLength;
export const allocatedPhoneNumberSelectByQueryOnlyMaxMin = _allocatedPhoneNumberSelectByQueryOnlyMaxMin;
export const allocatedPhoneNumberSelectOneObjectByQuery = _allocatedPhoneNumberSelectOneObjectByQuery;

// Auth Tokens Reducer
export const {
  slice: AuthTokensSlice,
  dbThunks: _authTokensThunks,
  selectByQuery: _authTokensSelectByQuery,
  selectOneObjectByQuery: _authTokensSelectOneObjectByQuery,
  selectPaginationStatus: _authTokensSelectPaginationStatus,
  selectors: _authTokensSelectors,
} = createDBServiceSlice<AuthToken & { _id: string }>({
  service: "auth-tokens",
  initialState: initialState,
  getReducerState: (state) => state.DBAuthTokensReducer,
});

export const authTokensReducerActions = {
  ..._authTokensThunks,
  ...AuthTokensSlice.actions,
};

export type AuthTokensReducerActions = keyof typeof authTokensReducerActions;
// Add code to export the reducer
export const DBAuthTokensReducer = AuthTokensSlice.reducer;

// WaNote Reducer
export const {
  slice: WaNoteSlice,
  dbThunks: _WaNoteThunks,
  selectByQuery: _waNoteSelectByQuery,
  selectOneObjectByQuery: _waNoteSelectOneObjectByQuery,
  selectors: _waNoteSelectors,
  selectByQueryOnlyLength: _waNotesSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _waNotesSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<WaNote>({
  service: "wa-notes",
  initialState: initialState,
  getReducerState: (state) => state.DBWaNoteReducer,
});
export const DBWaNoteReducer = WaNoteSlice.reducer;
export const WaNoteReducerActions = WaNoteSlice.actions;
export type waNoteReducerActions = keyof typeof WaNoteReducerActions;
export const DBWaNoteThunks = _WaNoteThunks;
export const waNoteSelectors = _waNoteSelectors;
export const waNoteSelectByQuery = _waNoteSelectByQuery;
export const waNoteSelectByQueryOnlyLength = _waNotesSelectByQueryOnlyLength;
export const waNoteSelectByQueryOnlyMaxMin = _waNotesSelectByQueryOnlyMaxMin;
export const waNoteSelectOneObjectByQuery = _waNoteSelectOneObjectByQuery;

// Email Sends Reducer (email-engine tracking)
export interface EmailSendFrontend {
  _id: string;
  userId: string;
  flowId: string | null;
  stepId: string | null;
  template: string;
  subject: string;
  recipient: string;
  type: 'transactional' | 'marketing';
  status: 'queued' | 'sent' | 'delivered' | 'bounced' | 'complained';
  trackingId: string;
  queuedAt: string;
  sentAt: string | null;
  deliveredAt: string | null;
  bouncedAt: string | null;
  complainedAt: string | null;
  error: string | null;
  opens: Array<{ at: string; ip: string; userAgent: string }>;
  firstOpenedAt: string | null;
  clicks: Array<{ at: string; url: string; ip: string; userAgent: string }>;
  firstClickedAt: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export const {
  slice: EmailSendSlice,
  dbThunks: _EmailSendThunks,
  selectByQuery: _emailSendSelectByQuery,
  selectOneObjectByQuery: _emailSendSelectOneObjectByQuery,
  selectors: _emailSendSelectors,
  selectByQueryOnlyLength: _emailSendSelectByQueryOnlyLength,
  selectByQueryOnlyMaxMin: _emailSendSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<EmailSendFrontend>({
  service: "email-sends",
  initialState: initialState,
  getReducerState: (state) => state.DBEmailSendReducer,
});

export const DBEmailSendReducer = EmailSendSlice.reducer;
export const EmailSendReducerActions = EmailSendSlice.actions;
export type emailSendReducerActions = keyof typeof EmailSendReducerActions;
export const DBEmailSendThunks = _EmailSendThunks;
export const emailSendSelectors = _emailSendSelectors;
export const emailSendSelectByQuery = _emailSendSelectByQuery;
export const emailSendSelectByQueryOnlyLength = _emailSendSelectByQueryOnlyLength;
export const emailSendSelectByQueryOnlyMaxMin = _emailSendSelectByQueryOnlyMaxMin;
export const emailSendSelectOneObjectByQuery = _emailSendSelectOneObjectByQuery;

/*
 * =================================================================
 * !!                    DO NOT PUT ANYTHING                        !!
 * !!                       BELOW HERE                             !!
 * =================================================================
 *
 *      |\      _,,,---,,_
 * ZZZzz /,`.-'`'    -.  ;-;;,_   Warning! Dragons below!
 *      |,4-  ) )-,_. ,\ (  `'-'
 *     '---''(_/--'  `-'\_)
 *
 * =================================================================
 */

// Must be declared after all the reducers!
export type DBSlice = ReturnType<typeof createDBServiceSlice>;
export const DBReducerMap: Map<string, DBSlice> = new Map(_DBReducerMap);
